/* eslint-disable @typescript-eslint/no-explicit-any -- Supabase JSON/RPC + composer boundary; see mem://preferences/lint-debt.md */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useUsage } from "@/hooks/useUsage";
import { sanitizeExcerptHtml } from "@/components/richTextEditorUtils";
import { resolveReturnToHref, type LibraryItem } from "@/lib/library";
import { shortCite } from "@/lib/citations";
import { normalizeUserText } from "@/lib/unicode";
import type { DetectedSourceIdentifier } from "@/lib/sourceIdentifiers";
import type { LibraryCategory } from "@/lib/libraryCategories";
import type { SourceComboboxOption } from "@/components/excerpts/SourceCombobox";

const toComboboxOption = (item: LibraryItem): SourceComboboxOption => ({
  id: item.id,
  title: item.title,
  subtitle: item.subtitle,
  display_title: item.display_title,
  author_text: item.author_text,
  source_type: item.source_type,
  container_title: item.container_title,
});

export function useExcerptEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { active } = useWorkspace();
  const { limits, refresh: refreshUsage } = useUsage();
  const isEdit = !!id;
  const requestedLibraryItemId = searchParams.get("libraryItem") ?? "";
  const returnTo = searchParams.get("returnTo") ?? "";
  const returnHref = useMemo(() => resolveReturnToHref(returnTo), [returnTo]);

  const [bodyHtml, setBodyHtml] = useState("");
  const [tagsManagerOpen, setTagsManagerOpen] = useState(false);
  const [bodyText, setBodyText] = useState("");
  const [pageStart, setPageStart] = useState<string>("");
  const [pageEnd, setPageEnd] = useState<string>("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [notes, setNotes] = useState("");
  const [libraryItemId, setLibraryItemId] = useState<string>("");
  const [libraryItems, setLibraryItems] = useState<LibraryItem[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [topics, setTopics] = useState<string[]>([]);
  const [inheritedCategories, setInheritedCategories] = useState<LibraryCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [addSourceOpen, setAddSourceOpen] = useState(false);
  const [addSourceQuery, setAddSourceQuery] = useState("");
  const [addSourceIdentifier, setAddSourceIdentifier] = useState<DetectedSourceIdentifier | null>(null);
  const [editSourceItem, setEditSourceItem] = useState<LibraryItem | null>(null);
  const [unlinkOpen, setUnlinkOpen] = useState(false);
  const [unlinkAlsoDelete, setUnlinkAlsoDelete] = useState(false);
  const [otherExcerptCount, setOtherExcerptCount] = useState<number | null>(null);
  const [unlinking, setUnlinking] = useState(false);

  const blockedByLimit = false;

  const comboboxOptions = useMemo(() => libraryItems.map(toComboboxOption), [libraryItems]);
  const selectedLibraryItem = useMemo(
    () => libraryItems.find((item) => item.id === libraryItemId) ?? null,
    [libraryItemId, libraryItems],
  );

  const citationPreview = useMemo(() => {
    if (!selectedLibraryItem) return null;
    const pageNumber = pageStart ? Number.parseInt(pageStart, 10) : null;
    return shortCite(selectedLibraryItem, Number.isFinite(pageNumber as number) ? (pageNumber as number) : null);
  }, [selectedLibraryItem, pageStart]);

  const citationIncomplete = useMemo(() => {
    if (!selectedLibraryItem) return false;
    const hasAuthor = !!(selectedLibraryItem.author_text?.trim() || (Array.isArray(selectedLibraryItem.authors) && selectedLibraryItem.authors.length));
    const yearFromDate = selectedLibraryItem.published_date?.match(/\b(\d{4})\b/)?.[1];
    const hasYear = !!(selectedLibraryItem.published_year || yearFromDate);
    return !hasAuthor || !hasYear;
  }, [selectedLibraryItem]);

  useEffect(() => {
    if (!active) return;
    supabase
      .from("library_items")
      .select("*")
      .eq("workspace_id", active.id)
      .order("updated_at", { ascending: false })
      .then(({ data }) => setLibraryItems((data as LibraryItem[]) ?? []));
  }, [active]);

  useEffect(() => {
    if (!requestedLibraryItemId || isEdit) return;
    if (libraryItemId === requestedLibraryItemId) return;
    setLibraryItemId(requestedLibraryItemId);
  }, [isEdit, libraryItemId, requestedLibraryItemId]);

  // One-shot prefill from query params (e.g. podcast transcript → new excerpt).
  // Only applied when creating a new excerpt; ignored when editing existing.
  useEffect(() => {
    if (isEdit) return;
    const prefillBody = searchParams.get("prefillBody");
    const prefillSourceUrl = searchParams.get("prefillSourceUrl");
    if (prefillBody && !bodyText) {
      const capped = prefillBody.slice(0, 8000);
      setBodyText(capped);
      const escaped = capped
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      setBodyHtml(`<p>${escaped}</p>`);
    }
    if (prefillSourceUrl && !sourceUrl) {
      setSourceUrl(prefillSourceUrl.slice(0, 2000));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit || !active) return;
    (async () => {
      const { data } = await supabase
        .from("excerpts")
        .select("*, excerpt_tags(tags(name)), excerpt_topics(content_framework_topics(label))")
        .eq("id", id!).single();
      if (!data) return;
      setBodyHtml(data.body_html); setBodyText(data.body_text);
      setPageStart(data.page_start?.toString() ?? "");
      setPageEnd(data.page_end?.toString() ?? "");
      setLibraryItemId((data as any).library_item_id ?? requestedLibraryItemId);
      setSourceUrl(data.source_url ?? "");
      const { data: decrypted } = await (supabase as any).rpc("get_decrypted_excerpts", {
        _workspace_id: active.id,
        _ids: [id!],
      });
      const decryptedNote = ((decrypted ?? []) as Array<{ id: string; notes: string | null }>)[0]?.notes ?? "";
      setNotes(decryptedNote ?? "");
      const tagNames = ((data as any).excerpt_tags ?? []).map((et: any) => et.tags?.name).filter(Boolean);
      setTags(tagNames);
      const topicNames = ((data as any).excerpt_topics ?? []).map((et: any) => et.content_framework_topics?.label).filter(Boolean);
      setTopics(topicNames);
    })();
  }, [id, isEdit, active, requestedLibraryItemId]);

  useEffect(() => {
    if (!libraryItemId) {
      setInheritedCategories([]);
      return;
    }
    (async () => {
      const { data } = await supabase
        .from("library_item_categories")
        .select("library_categories(id, name, color, workspace_id, created_at, updated_at)")
        .eq("library_item_id", libraryItemId);
      const cats = ((data as any) ?? [])
        .map((row: any) => row.library_categories)
        .filter(Boolean) as LibraryCategory[];
      setInheritedCategories(cats.sort((a, b) => a.name.localeCompare(b.name)));
    })();
  }, [libraryItemId]);

  const save = async () => {
    if (!active) return;
    if (!bodyText.trim()) return toast.error("Excerpt cannot be empty");
    if (blockedByLimit) { setUpgradeOpen(true); return; }
    setLoading(true);

    const payload = {
      workspace_id: active.id,
      body_html: normalizeUserText(sanitizeExcerptHtml(bodyHtml)),
      body_text: normalizeUserText(bodyText),
      page_start: pageStart ? parseInt(pageStart) : null,
      page_end: pageEnd ? parseInt(pageEnd) : null,
      library_item_id: libraryItemId || null,
      source_url: sourceUrl ? normalizeUserText(sourceUrl) : null,
    };

    let excerptId = id;
    if (isEdit) {
      const { error } = await supabase.from("excerpts").update(payload).eq("id", id!);
      if (error) { toast.error(error.message); setLoading(false); return; }
    } else {
      const { data, error } = await supabase.from("excerpts").insert(payload).select("id").single();
      if (error) { toast.error(error.message); setLoading(false); return; }
      excerptId = data.id;
    }

    if (excerptId) {
      const { error: notesError } = await (supabase as any).rpc("set_excerpt_notes", {
        _excerpt_id: excerptId,
        _notes: notes ? normalizeUserText(notes) : null,
      });
      if (notesError) { toast.error(notesError.message); setLoading(false); return; }
    }

    if (excerptId) {
      await supabase.from("excerpt_tags").delete().eq("excerpt_id", excerptId);
      for (const name of tags) {
        let tagId: string;
        const { data: existing } = await supabase.from("tags").select("id").eq("workspace_id", active.id).ilike("name", name).maybeSingle();
        if (existing) tagId = existing.id;
        else {
          const { data, error } = await supabase.from("tags").insert({ workspace_id: active.id, name }).select("id").single();
          if (error) continue;
          tagId = data.id;
        }
        await supabase.from("excerpt_tags").insert({ excerpt_id: excerptId, tag_id: tagId });
      }

      await supabase.from("excerpt_topics").delete().eq("excerpt_id", excerptId);
      if (active.codex_enabled !== false) {
        for (const name of topics) {
          const { data: existing } = await supabase
            .from("content_framework_topics")
            .select("id")
            .eq("workspace_id", active.id)
            .ilike("label", name)
            .maybeSingle();
          if (!existing) continue;
          await supabase
            .from("excerpt_topics")
            .insert({ excerpt_id: excerptId, topic_id: (existing as any).id });
        }
      }
    }

    setLoading(false);
    refreshUsage();
    toast.success(isEdit ? "Excerpt updated" : "Excerpt added");
    const next = new URLSearchParams();
    if (libraryItemId || requestedLibraryItemId) next.set("libraryItem", libraryItemId || requestedLibraryItemId);
    if (returnTo) next.set("returnTo", returnTo);
    const query = next.toString();
    navigate(`/excerpts/${excerptId}${query ? `?${query}` : ""}`);
  };

  const handleOpenAddSource = (initialQuery: string, identifier?: DetectedSourceIdentifier | null) => {
    setEditSourceItem(null);
    setAddSourceQuery(initialQuery);
    setAddSourceIdentifier(identifier ?? null);
    setAddSourceOpen(true);
  };

  const handleOpenEditSource = () => {
    if (!selectedLibraryItem) return;
    setAddSourceQuery("");
    setAddSourceIdentifier(null);
    setEditSourceItem(selectedLibraryItem);
    setAddSourceOpen(true);
  };

  const handleSourceSaved = (item: LibraryItem) => {
    setLibraryItems((prev) => {
      const exists = prev.some((existing) => existing.id === item.id);
      if (exists) return prev.map((existing) => (existing.id === item.id ? item : existing));
      return [item, ...prev];
    });
    setLibraryItemId(item.id);
    setEditSourceItem(null);
  };

  const openUnlinkDialog = async () => {
    if (!libraryItemId) return;
    setUnlinkAlsoDelete(false);
    setOtherExcerptCount(null);
    setUnlinkOpen(true);
    if (!active) return;
    let query = supabase
      .from("excerpts")
      .select("id", { count: "exact", head: true })
      .eq("workspace_id", active.id)
      .eq("library_item_id", libraryItemId);
    if (isEdit && id) query = query.neq("id", id);
    const { count } = await query;
    setOtherExcerptCount(count ?? 0);
  };

  const performUnlink = async () => {
    if (!libraryItemId || !active) return;
    setUnlinking(true);
    const targetItemId = libraryItemId;
    try {
      if (isEdit && id) {
        const { error } = await supabase
          .from("excerpts")
          .update({ library_item_id: null })
          .eq("id", id);
        if (error) throw error;
      }
      if (unlinkAlsoDelete) {
        const { error } = await supabase
          .from("library_items")
          .delete()
          .eq("id", targetItemId);
        if (error) throw error;
        setLibraryItems((prev) => prev.filter((item) => item.id !== targetItemId));
      }
      setLibraryItemId("");
      setUnlinkOpen(false);
      toast.success(unlinkAlsoDelete ? "Source unlinked and deleted" : "Source unlinked");
    } catch (err: any) {
      toast.error(err?.message ?? "Failed to unlink source");
    } finally {
      setUnlinking(false);
    }
  };

  return {
    id, isEdit, navigate, active, returnTo, returnHref, requestedLibraryItemId,
    bodyHtml, setBodyHtml, bodyText, setBodyText,
    pageStart, setPageStart, pageEnd, setPageEnd, sourceUrl, setSourceUrl,
    notes, setNotes, libraryItemId, setLibraryItemId, libraryItems,
    tags, setTags, topics, setTopics, inheritedCategories,
    loading, blockedByLimit, limits, upgradeOpen, setUpgradeOpen,
    addSourceOpen, setAddSourceOpen, addSourceQuery, addSourceIdentifier, editSourceItem, setEditSourceItem,
    unlinkOpen, setUnlinkOpen, unlinkAlsoDelete, setUnlinkAlsoDelete, otherExcerptCount, unlinking,
    tagsManagerOpen, setTagsManagerOpen,
    comboboxOptions, selectedLibraryItem, citationPreview, citationIncomplete,
    save, handleOpenAddSource, handleOpenEditSource, handleSourceSaved,
    openUnlinkDialog, performUnlink,
  };
}
